import { useEffect } from 'react';
import { Box, ActionIcon, Tooltip, Group } from '@mantine/core';
import { IconMicrophone, IconMicrophoneOff, IconVideo, IconVideoOff, IconPhoneOff } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useRoomStore } from '@/stores/roomStore';

interface VideoControlsProps {
  isMicOn: boolean;
  isCameraOn: boolean;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onLeave: () => void;
  isOwner?: boolean;  // Owner should use "Encerrar Conversa" instead of leave button
}

export const VideoControls = ({
  isMicOn,
  isCameraOn,
  onToggleMic,
  onToggleCamera,
  onLeave,
  isOwner = false,
}: VideoControlsProps) => {
  const navigate = useNavigate();
  const clearRoom = useRoomStore((state) => state.clearRoom);

  const handleLeave = async () => {
    try {
      await onLeave();
      clearRoom(); // Clear room state to prevent stale data
      navigate('/dashboard');
    } catch (error) {
      console.error('Error leaving call:', error);
      notifications.show({
        title: 'Erro',
        message: 'Erro ao sair da chamada',
        color: 'red',
      });
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignore if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === 'm' || e.key === 'M') {
        onToggleMic();
      }
      if (e.key === 'v' || e.key === 'V') {
        onToggleCamera();
      }
      // Only allow L shortcut for non-owners
      if (!isOwner && (e.key === 'l' || e.key === 'L')) {
        handleLeave();
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [onToggleMic, onToggleCamera, isOwner]);

  return (
    <Box
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(10px)',
        padding: '16px 24px',
        borderRadius: 24,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      }}
    >
      <Group gap="md">
        {/* Microphone */}
        <Tooltip label={`${isMicOn ? 'Desativar' : 'Ativar'} microfone (M)`}>
          <ActionIcon
            size={56}
            radius="xl"
            variant="filled"
            color={isMicOn ? 'blue' : 'red'}
            onClick={onToggleMic}
            aria-label={`${isMicOn ? 'Desativar' : 'Ativar'} microfone`}
            aria-pressed={isMicOn}
            style={{
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {isMicOn ? <IconMicrophone size={28} /> : <IconMicrophoneOff size={28} />}
          </ActionIcon>
        </Tooltip>

        {/* Camera */}
        <Tooltip label={`${isCameraOn ? 'Desativar' : 'Ativar'} câmera (V)`}>
          <ActionIcon
            size={56}
            radius="xl"
            variant="filled"
            color={isCameraOn ? 'blue' : 'red'}
            onClick={onToggleCamera}
            aria-label={`${isCameraOn ? 'Desativar' : 'Ativar'} câmera`}
            aria-pressed={isCameraOn}
            style={{
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {isCameraOn ? <IconVideo size={28} /> : <IconVideoOff size={28} />}
          </ActionIcon>
        </Tooltip>

        {/* Leave - hidden for owner (should use "Encerrar Conversa" button instead) */}
        {!isOwner && (
          <Tooltip label="Sair da chamada (L)">
            <ActionIcon
              size={56}
              radius="xl"
              variant="filled"
              color="red"
              onClick={handleLeave}
              aria-label="Sair da chamada"
              style={{
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <IconPhoneOff size={28} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Box>
  );
};
